import { Link } from 'react-router-dom';
import {
    CheckCircle2Icon,
    EyeIcon,
    EyeOffIcon,
    PlusIcon,
    StoreIcon,
    XCircleIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useFetch } from '@/hooks/useFecth';
import { useClub } from './ClubContext';
import ClubEmptyList from './components/ClubEmptyList';
import ClubSettingsCardHeader from './components/ClubSettingsCardHeader';
import PlanDialog from './components/PlanDialog';

function formatCurrency(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatPlanPrice(item) {
    if (item?.isFree) return 'Grátis';
    return formatCurrency(item?.price);
}

function sellsCycle(item, cycle) {
    const hasYearly = Number(item?.yearlyPrice) > 0;
    const cycles = Array.isArray(item?.cycles) && item.cycles.length ? item.cycles : ['MONTHLY', ...(hasYearly ? ['YEARLY'] : [])];
    return cycles.includes(cycle) && (cycle !== 'YEARLY' || hasYearly);
}

function planTypeLabel(item) {
    if (item?.isFree) return 'Gratuito';
    if (item?.isTrial) {
        const days = item.trialDays || '';
        return item.trialPayment === false ? `Teste ${days}d sem cobrança` : `Teste ${days}d`;
    }
    return 'Pago';
}

function PlanStatusBadge({ active }) {
    if (active) {
        return (
            <Badge variant="default" className="gap-1">
                <CheckCircle2Icon className="size-3" />
                Ativo
            </Badge>
        );
    }
    return (
        <Badge variant="outline" className="gap-1 text-muted-foreground">
            <XCircleIcon className="size-3" />
            Inativo
        </Badge>
    );
}

function PlanVisibilityBadge({ visibility }) {
    const isPublic = visibility === 'PUBLIC';
    if (isPublic) {
        return (
            <Badge variant="secondary" className="gap-1">
                <EyeIcon className="size-3" />
                Público
            </Badge>
        );
    }
    return (
        <Badge variant="outline" className="gap-1">
            <EyeOffIcon className="size-3" />
            Privado
        </Badge>
    );
}

function ProductsClubList({ data, mutate }) {
    const { health, isReseller } = useClub();
    const attention = health?.checks?.plans?.ok === false;
    const plans = Array.isArray(data) ? data : [];

    const summary = {
        total: plans.length,
        active: plans.filter(item => item.status !== false).length,
        inactive: plans.filter(item => item.status === false).length,
        public: plans.filter(item => item.visibility === 'PUBLIC').length,
        private: plans.filter(item => item.visibility !== 'PUBLIC').length,
    };

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                    <ClubSettingsCardHeader
                        icon={StoreIcon}
                        title="Planos"
                        description={
                            isReseller
                                ? 'Crie e gerencie planos de assinatura: preços, visibilidade e status.'
                                : 'Crie e gerencie os pacotes de benefícios e o status de cada plano.'
                        }
                        attention={attention}
                    />
                    <Button variant="outline" size="sm" render={<Link to="#planos" />} className="shrink-0">
                        <PlusIcon className="size-4" />
                        Adicionar plano
                    </Button>
                </CardHeader>
            </Card>

            {plans.length > 0 ? (
                <div
                    className={
                        isReseller
                            ? 'grid grid-cols-2 gap-3 sm:grid-cols-4'
                            : 'grid grid-cols-2 gap-3'
                    }
                >
                    <Card>
                        <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground">Ativos</p>
                            <p className="text-2xl font-semibold tabular-nums">{summary.active}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground">Inativos</p>
                            <p className="text-2xl font-semibold tabular-nums">{summary.inactive}</p>
                        </CardContent>
                    </Card>
                    {isReseller ? (
                        <>
                            <Card>
                                <CardContent className="p-4">
                                    <p className="text-xs text-muted-foreground">Públicos</p>
                                    <p className="text-2xl font-semibold tabular-nums">{summary.public}</p>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-4">
                                    <p className="text-xs text-muted-foreground">Privados</p>
                                    <p className="text-2xl font-semibold tabular-nums">{summary.private}</p>
                                </CardContent>
                            </Card>
                        </>
                    ) : null}
                </div>
            ) : null}

            <Card>
                <CardContent className="p-0">
                    {!plans.length ? (
                        <div className="flex flex-col items-center gap-4 py-4">
                            <ClubEmptyList
                                title="Nenhum plano cadastrado"
                                description={
                                    isReseller
                                        ? 'Crie o primeiro plano para oferecer assinaturas aos membros.'
                                        : 'Crie o primeiro plano para montar os pacotes de benefícios.'
                                }
                            />
                            <Button variant="outline" size="sm" render={<Link to="#planos" />}>
                                <PlusIcon className="size-4" />
                                Criar plano
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/40 text-left">
                                        <th className="px-4 py-3 font-medium">Plano</th>
                                        <th className="px-4 py-3 font-medium">Ordem</th>
                                        {isReseller ? (
                                            <>
                                                <th className="px-4 py-3 font-medium">Preço</th>
                                                <th className="px-4 py-3 font-medium">Tipo</th>
                                                <th className="px-4 py-3 font-medium">Visibilidade</th>
                                            </>
                                        ) : null}
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 text-right font-medium">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {plans.map(item => {
                                        const active = item.status !== false;
                                        return (
                                            <tr
                                                key={item.id}
                                                className="border-b last:border-b-0 hover:bg-muted/30"
                                            >
                                                <td className="px-4 py-3">
                                                    <div className="min-w-0">
                                                        <p className="font-medium">{item.name}</p>
                                                        {item.sku ? (
                                                            <p className="mt-0.5 text-xs text-muted-foreground">
                                                                SKU {item.sku}
                                                            </p>
                                                        ) : null}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 tabular-nums">{item.order ?? 0}</td>
                                                {isReseller ? (
                                                    <>
                                                        <td className="px-4 py-3 tabular-nums">
                                                            {item.isFree || sellsCycle(item, 'MONTHLY') ? (
                                                                <>
                                                                    {formatPlanPrice(item)}
                                                                    {!item.isFree ? (
                                                                        <span className="text-xs text-muted-foreground">
                                                                            {' '}
                                                                            /mês
                                                                        </span>
                                                                    ) : null}
                                                                </>
                                                            ) : null}
                                                            {!item.isFree && sellsCycle(item, 'YEARLY') ? (
                                                                <p className="text-xs text-muted-foreground">
                                                                    {formatCurrency(item.yearlyPrice)}/ano
                                                                    {item.installmentEnabled && Number(item.maxInstallments) >= 2
                                                                        ? ` · em até ${item.maxInstallments}x no cartão`
                                                                        : ''}
                                                                </p>
                                                            ) : null}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <Badge variant="secondary">
                                                                {planTypeLabel(item)}
                                                            </Badge>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <PlanVisibilityBadge
                                                                visibility={item.visibility}
                                                            />
                                                        </td>
                                                    </>
                                                ) : null}
                                                <td className="px-4 py-3">
                                                    <PlanStatusBadge active={active} />
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        render={<Link to={`#planos|${item.id}`} />}
                                                    >
                                                        Editar
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <PlanDialog mutate={mutate} />
        </div>
    );
}

export default function ProductsClub() {
    const { data, mutate } = useFetch('club/settings/plans');
    const { reloadHealth } = useClub();

    const refresh = async () => {
        await mutate();
        await reloadHealth?.();
    };

    return <ProductsClubList data={data} mutate={refresh} />;
}
